import {
  EmptyStateIconStyled,
  EmptyStateWrapperStyled,
  StackEmptyStateStyled,
} from './styles/EmptyStateComponentStyles';
import { getTestAttrs, TestIdContext } from '../../../constants/testingAttributes';
import useGetPaletteFromWhiteLabel from '../../../hooks/useGetPaletteFromWhiteLabel';
import palette from '../../../theme/learnerPalette';
import AtomButton from '../../atoms/AtomButton/AtomButton';
import AtomFlex from '../../atoms/AtomFlex';
import AtomTypography, { FontWeightType } from '../../atoms/AtomTypography/AtomTypography';
import PlusIcon from '../../icons/svg/PlusIcon';
import { styleConfigs } from '../../resources/styleConfigs';
import BoxWrapper from '../../wrappers/BoxWrapper';

type EmptyStateTitleComponent = 'p' | 'h2';

interface IEmptyStateComponentProps {
  'data-cy'?: string;
  titleComponent?: EmptyStateTitleComponent;
  svgIconComponent?: JSX.Element;
  iconSrc?: string;
  alt?: string;
  ariaLabel?: string;
  widthIcon?: number | string;
  heightIcon?: number | string;
  iconLineWidth?: number;
  iconColor?: string;
  title?: string;
  fontSizeTitle?: string;
  colorTitle?: string;
  fontWeightTitle?: FontWeightType;
  letterSpacingTitle?: string | number;
  lineHeightTitle?: string | number;
  marginTopStackIcon?: number;
  marginBottomStackIcon?: number;
  directionIconTitle?: string;
  gapIconTitle?: number | string;
  subtitle?: string;
  fontSizeSubtitle?: string;
  colorSubtitle?: string;
  fontWeightSubtitle?: FontWeightType;
  letterSpacingSubtitle?: string | number;
  lineHeightSubtitle?: string | number;
  paddingTopSubtitle?: string | number;
  maxWidthSubtitle?: string | number;
  startIcon?: boolean;
  callToActionButton?: {
    buttonText: string;
    buttonClick: VoidFunction;
  };
  gapTitlesButton?: number;
}

const EmptyStateComponent: React.FC<IEmptyStateComponentProps> = (props) => {
  const {
    'data-cy': dataCy,
    titleComponent = 'p',
    svgIconComponent,
    iconSrc,
    alt,
    ariaLabel,
    widthIcon = 'auto !important',
    heightIcon = 'auto !important',
    iconLineWidth,
    iconColor = palette.light.text.prussianBlue,
    title,
    fontSizeTitle,
    colorTitle = palette.light.text.prussianBlue,
    fontWeightTitle = 700,
    letterSpacingTitle,
    lineHeightTitle,
    marginTopStackIcon,
    marginBottomStackIcon,
    directionIconTitle,
    gapIconTitle = styleConfigs.marginPadding.custom6,
    subtitle,
    fontSizeSubtitle,
    colorSubtitle = palette.light.text.mistBlue,
    fontWeightSubtitle = 400,
    letterSpacingSubtitle,
    lineHeightSubtitle,
    paddingTopSubtitle = styleConfigs.marginPadding.empty,
    maxWidthSubtitle = styleConfigs.dimensions.width450,
    startIcon,
    callToActionButton,
    gapTitlesButton,
  } = props;
  const wlPalette = useGetPaletteFromWhiteLabel();
  const buttonColorPalette = wlPalette?.text?.redViolet ?? palette.light.text.redViolet;

  return (
    <EmptyStateWrapperStyled
      gaptitlesbutton={gapTitlesButton}
      {...getTestAttrs(TestIdContext.HOME, 'empty_state_wrapper')}
      data-cy={dataCy}
    >
      <AtomFlex>
        <StackEmptyStateStyled
          margintopstackicon={marginTopStackIcon}
          marginbottomstackicon={marginBottomStackIcon}
          directionicontitle={directionIconTitle}
          gapicontitle={gapIconTitle}
        >
          {svgIconComponent ?? (
            <EmptyStateIconStyled
              src={iconSrc ?? ''}
              alt={alt}
              aria-label={ariaLabel}
              widthicon={widthIcon}
              heighticon={heightIcon}
              iconlinewidth={iconLineWidth}
              iconcolor={iconColor}
            />
          )}
          {title && (
            <AtomTypography
              component={titleComponent}
              fontFamily={styleConfigs.fonts.fontFamily}
              fontWeight={fontWeightTitle}
              fontSize={fontSizeTitle}
              color={colorTitle}
              letterSpacing={letterSpacingTitle}
              lineHeight={lineHeightTitle}
            >
              {title}
            </AtomTypography>
          )}
        </StackEmptyStateStyled>
        {subtitle && (
          <AtomTypography
            component="p"
            fontFamily={styleConfigs.fonts.fontFamily}
            fontWeight={fontWeightSubtitle}
            fontSize={fontSizeSubtitle}
            color={colorSubtitle}
            letterSpacing={letterSpacingSubtitle}
            lineHeight={lineHeightSubtitle}
            sx={{
              paddingTop: paddingTopSubtitle,
              maxWidth: maxWidthSubtitle,
              textAlign: 'center',
              margin: `${styleConfigs.marginPadding.empty} ${styleConfigs.marginPadding.auto}`,
            }}
          >
            {subtitle}
          </AtomTypography>
        )}
      </AtomFlex>
      {callToActionButton && (
        <BoxWrapper>
          <AtomButton
            variant="outlined"
            startIcon={
              startIcon && (
                <PlusIcon width={styleConfigs.dimensions.width18} height={styleConfigs.dimensions.height18} />
              )
            }
            onClick={callToActionButton.buttonClick}
            data-testid="empty_state_action_button"
            data-cy-button-type="empty_state_action"
            data-cy-has-start-icon={!!startIcon}
            sx={{
              fontFamily: `${styleConfigs.fonts.fontFamily} !important`,
              fontWeight: `${styleConfigs.fonts.regular} !important`,
              fontSize: `${styleConfigs.fonts.fontSize14} !important`,
              color: `${buttonColorPalette} !important`,
              padding: `${styleConfigs.marginPadding.custom6}px ${styleConfigs.marginPadding.custom16}px !important`,
              border: `${styleConfigs.borders.borderLine} ${buttonColorPalette} !important`,
              borderRadius: `${styleConfigs.borders.radius8rem} !important`,
            }}
          >
            {callToActionButton.buttonText}
          </AtomButton>
        </BoxWrapper>
      )}
    </EmptyStateWrapperStyled>
  );
};

export default EmptyStateComponent;
